import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";
import matchingService, { MatchStats } from "../services/matchingService";

const MatchingDashboard: React.FC = () => {
  const [stats, setStats] = useState<MatchStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [matchingOptions, setMatchingOptions] = useState({
    threshold: 70,
    autoApproveThreshold: 90,
    days: 90,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await matchingService.getStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  const handleRunMatching = async () => {
    try {
      setMatching(true);
      setError(null);
      setSuccess(null);

      const result = await matchingService.runAutoMatching({
        dryRun: false,
        threshold: matchingOptions.threshold,
        autoApproveThreshold: matchingOptions.autoApproveThreshold,
        days: matchingOptions.days,
      });

      setSuccess(
        `Matching completo! ${result.summary.matches_created} matches criados (${result.summary.auto_approved} auto-aprovados, ${result.summary.pending_approval} pendentes)`
      );
      setOpenDialog(false);

      // Reload stats
      await loadStats();
    } catch (err: any) {
      setError(err.message || "Erro ao executar matching");
    } finally {
      setMatching(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Alert severity="error">Não foi possível carregar as estatísticas</Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Matching de Faturas e Movimentos
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadStats}
            disabled={matching}
            sx={{ mr: 1 }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => setOpenDialog(true)}
            disabled={matching}
          >
            Executar Matching
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Invoices Stats */}
        <Box>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Faturas
              </Typography>
              <Typography variant="h4" component="div" gutterBottom>
                {stats.invoices.total}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Box>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${stats.invoices.matched} matchadas`}
                    color="success"
                    size="small"
                  />
                </Box>
                <Box>
                  <Chip
                    icon={<CancelIcon />}
                    label={`${stats.invoices.unmatched} por matchear`}
                    color="warning"
                    size="small"
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Taxa de matching: {stats.invoices.match_percentage}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Valor total: €{Number(stats.invoices.total_value).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Transactions Stats */}
        <Box>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Movimentos Bancários
              </Typography>
              <Typography variant="h4" component="div" gutterBottom>
                {stats.transactions.total}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Box>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${stats.transactions.matched} matchados`}
                    color="success"
                    size="small"
                  />
                </Box>
                <Box>
                  <Chip
                    icon={<CancelIcon />}
                    label={`${stats.transactions.unmatched} por matchear`}
                    color="warning"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Matches Stats */}
        <Box>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Matches
              </Typography>
              <Typography variant="h4" component="div" gutterBottom>
                {stats.matches.by_status.pending +
                  stats.matches.by_status.approved +
                  stats.matches.by_status.rejected}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1} mt={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Pendentes:</Typography>
                  <Chip
                    icon={<PendingIcon />}
                    label={stats.matches.by_status.pending}
                    color="warning"
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Aprovados:</Typography>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={stats.matches.by_status.approved}
                    color="success"
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Rejeitados:</Typography>
                  <Chip
                    icon={<CancelIcon />}
                    label={stats.matches.by_status.rejected}
                    color="error"
                    size="small"
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Score médio: {stats.matches.avg_confidence_score}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Match Types */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Distribuição por Tipo
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>Automáticos:</Typography>
            <Chip
              label={stats.matches.by_type.automatic}
              color="success"
              variant="outlined"
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>Sugeridos:</Typography>
            <Chip
              label={stats.matches.by_type.suggested}
              color="info"
              variant="outlined"
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>Manuais:</Typography>
            <Chip
              label={stats.matches.by_type.manual}
              color="default"
              variant="outlined"
            />
          </Box>
        </Box>
      </Paper>

      {/* Matching Options Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Opções de Matching Automático</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Threshold Mínimo (%)"
              type="number"
              value={matchingOptions.threshold}
              onChange={(e) =>
                setMatchingOptions({
                  ...matchingOptions,
                  threshold: Number(e.target.value),
                })
              }
              helperText="Score mínimo para criar match (recomendado: 70)"
              fullWidth
            />
            <TextField
              label="Auto-Aprovar Acima de (%)"
              type="number"
              value={matchingOptions.autoApproveThreshold}
              onChange={(e) =>
                setMatchingOptions({
                  ...matchingOptions,
                  autoApproveThreshold: Number(e.target.value),
                })
              }
              helperText="Matches acima deste score são aprovados automaticamente (recomendado: 90)"
              fullWidth
            />
            <TextField
              label="Considerar Últimos (dias)"
              type="number"
              value={matchingOptions.days}
              onChange={(e) =>
                setMatchingOptions({
                  ...matchingOptions,
                  days: Number(e.target.value),
                })
              }
              helperText="Apenas movimentos dos últimos N dias"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleRunMatching}
            variant="contained"
            disabled={matching}
          >
            {matching ? "A processar..." : "Executar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MatchingDashboard;
